import type { ITokenUsageFilterParams } from "./token-usage-filters";
import type {
  IGetUserAnalyticsBaseParams,
  IUserAnalyticsUserIdentity,
} from "./token-usage-user-analytics";

// NOTE: NSAI-8010 — mirrors the server's inventory contract. Counts are owned
// by the creator only, and are a snapshot of what exists now.
export type TUserInventoryIdleDays = 30 | 60 | 90;

export type TUserInventorySortBy =
  | "agents"
  | "webhooks"
  | "hooks"
  | "workflows"
  | "assistants"
  | "dashboards"
  | "chats";

export type TUserInventorySortOrder = "asc" | "desc";

export enum EUserInventoryResourceKind {
  AGENT = "agent",
  ASSISTANT = "assistant",
  DASHBOARD = "dashboard",
  WORKFLOW = "workflow",
  HOOK = "hook",
  WEBHOOK = "webhook",
}

export interface IUserInventoryIdleCounts {
  agents: number;
  webhooks: number;
  hooks: number;
  workflows: number;
  assistants: number;
  dashboards: number;
}

export interface IUserInventoryIntegrationCalls {
  provider: string;
  chatCalls: number;
  agentCalls: number;
}

export interface IUserInventoryRow extends IUserAnalyticsUserIdentity {
  userUuid: string;
  // NOTE: Removed from the organization; still listed for what they left
  // behind, which an owner may want to clean up.
  isRemoved: boolean;
  agents: number;
  webhooks: number;
  hooks: number;
  workflows: number;
  assistants: number;
  dashboards: number;
  chats: number;
  idle: IUserInventoryIdleCounts;
  topIntegration: IUserInventoryIntegrationCalls | null;
}

export interface IGetUserInventoryParams extends ITokenUsageFilterParams {
  organizationUuid: string;
  page?: number;
  limitPerPage?: number;
  idleDays?: TUserInventoryIdleDays;
  sortBy?: TUserInventorySortBy;
  sortOrder?: TUserInventorySortOrder;
}

export interface IGetUserInventoryResponse {
  totalUsers: number;
  page: number;
  limitPerPage: number;
  idleDays: TUserInventoryIdleDays;
  // NOTE: The first UTC day chat calls were counted for the organization
  // (YYYY-MM-DD); null before any call. Earlier chat use is not in the figures.
  chatIntegrationsTrackedSince: string | null;
  // NOTE: Chat calls have no project, so under a project filter they are still
  // the person's organization-wide total.
  areChatCallsOrganizationWide: boolean;
  rows: IUserInventoryRow[];
}

export interface IUserInventoryOwnedResource {
  kind: EUserInventoryResourceKind;
  uuid: string;
  name: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  isIdle: boolean;
}

// NOTE: One kind of what a person owns: how many, how many sat idle, and the
// first of them (up to 50), longest unused first, so the idle ones lead.
export interface IUserInventoryResourceGroup {
  kind: EUserInventoryResourceKind;
  total: number;
  idle: number;
  resources: IUserInventoryOwnedResource[];
}

export interface IGetUserInventoryDetailsParams {
  organizationUuid: string;
  userUuid: string;
  projectUuids?: string;
  idleDays?: TUserInventoryIdleDays;
  dateFrom?: string;
  dateTo?: string;
}

export interface IGetUserInventoryDetailsResponse {
  idleDays: TUserInventoryIdleDays;
  groups: IUserInventoryResourceGroup[];
  areChatCallsOrganizationWide: boolean;
  integrations: IUserInventoryIntegrationCalls[];
}

export interface IUserInventoryColumn {
  sortBy: TUserInventorySortBy;
  labelKey: string;
}

export interface ISumInventoryCallsProps {
  chatCalls: number;
  agentCalls: number;
}

// NOTE: A kind a person can own, listed in the inventory's "Owns" cell. Chats
// have a column of their own, so they are not one.
export type TUserInventoryHoldingKind = Exclude<TUserInventorySortBy, "chats">;

export interface IUserInventoryHolding {
  kind: TUserInventoryHoldingKind;
  labelKey: `tokenCostUsage.userAnalytics.inventory.columns.${TUserInventoryHoldingKind}`;
  count: number;
}

export interface IUserInventoryIdleSummary {
  idle: number;
  total: number;
  percent: number;
}

export interface ISummarizeInventoryIdleProps {
  row: IUserInventoryRow;
}

export interface IListInventoryHoldingsProps {
  row: IUserInventoryRow;
}

export interface IUserInventoryIdleBreakdownItem {
  kind: TUserInventoryHoldingKind;
  labelKey: `tokenCostUsage.userAnalytics.inventory.columns.${TUserInventoryHoldingKind}`;
  idle: number;
  total: number;
}

export interface IListInventoryIdleBreakdownProps {
  row: IUserInventoryRow;
}

// NOTE: How a kind of resource is used, which picks the words for its last
// use: an agent or workflow runs, a hook or webhook is triggered, an assistant
// or dashboard is used.
export type TUserInventoryUseVerb = "run" | "triggered" | "used";

export interface IUserInventoryResourceUse {
  labelKey: `tokenCostUsage.userAnalytics.inventory.details.use.${TUserInventoryUseVerb}.${"last" | "never"}`;
  date: string;
}

export interface IDescribeInventoryResourceUseProps {
  resource: IUserInventoryOwnedResource;
}

export interface IListKnownInventoryGroupsProps {
  groups: IUserInventoryResourceGroup[];
}

export interface IIsSameUserInventoryScopeProps {
  previous: IGetUserInventoryParams;
  next: IGetUserInventoryParams;
}

export interface IBuildUserInventoryParamsProps {
  params: IGetUserAnalyticsBaseParams;
  page: number;
  rowsPerPage: number;
  idleDays: TUserInventoryIdleDays;
  sortBy: TUserInventorySortBy;
  sortOrder: TUserInventorySortOrder;
}

export interface IBuildUserInventoryDetailsParamsProps {
  params: IGetUserAnalyticsBaseParams;
  userUuid: string;
  idleDays: TUserInventoryIdleDays;
}

export interface IUserInventorySort {
  sortBy: TUserInventorySortBy;
  sortOrder: TUserInventorySortOrder;
}

export interface IResolveNextInventorySortProps {
  current: IUserInventorySort;
  column: TUserInventorySortBy;
}

export interface IFormatInventoryDateProps {
  value: string;
  language: string;
}
